import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import ChatPanel from "./ChatPanel";

/**
 * Floating action button that opens the document chat drawer.
 *
 * Visible on the Analysis page for any analyzed document, on all 6 doc
 * types. Mobile: icon-only. Desktop: icon + label.
 *
 * Guardian C2 — When the URL hash equals `#chat` (e.g. coming from a
 * "Fix now" intelligent action), the panel auto-opens once.
 */
export default function ChatFab({ documentId, documentType, cachedSuggestions, onCitationJump }) {
  const { t } = useTranslation("chat");
  const location = useLocation();
  // Auto-open when the page is reached with #chat (e.g. from "Fix now").
  const [open, setOpen] = useState(
    () => typeof window !== "undefined" && window.location?.hash === "#chat" && !!documentId,
  );

  useEffect(() => {
    if (location.hash === "#chat" && typeof window !== "undefined" && window.history?.replaceState) {
      window.history.replaceState(null, "", location.pathname + location.search);
    }
  }, [location.hash, location.pathname, location.search]);

  if (!documentId) return null;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-40 inline-flex items-center gap-2 px-4 py-3 rounded-full bg-zinc-950 hover:bg-zinc-800 text-white shadow-xl hover:shadow-2xl transition-all hover:-translate-y-0.5"
        data-testid="chat-fab"
        aria-label={t("fab.label")}
      >
        <span className="relative grid place-items-center">
          <MessageCircle size={16} strokeWidth={2} />
          <Sparkles
            size={9}
            strokeWidth={2.5}
            className="absolute -top-1 -right-1.5 text-amber-300"
          />
        </span>
        <span className="hidden sm:inline text-sm font-medium">{t("fab.label")}</span>
      </button>

      {open && (
        <ChatPanel
          documentId={documentId}
          documentType={documentType}
          cachedSuggestions={cachedSuggestions}
          onCitationJump={onCitationJump}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
